import { graphql } from 'gatsby';

import { SEO } from 'components';
import { Container, Section } from 'components/GridV2';
import { About, Benefits, Description, Hero, Instructions, Resources } from 'components/pages/new-to-canada';
import { FAQ } from 'components/rebrand/FAQ';
import { Layout } from 'components/rebrand/Layout';
import { SignUp } from 'components/rebrand/SignUp';

import { INewToCanadaPageProps } from 'typescriptDefs/pages/new-to-canada';

import { afTrackingByPage } from 'helpers/getAfTracking';
import useAddCookiesFromUrlQueryParams from 'helpers/useAddCookieQueryParams';

const PageNewToCanada = ({
  data: {
    page: { about, benefits, deeplink, description, faq, hero, metadata, signUp, ...page },
  },
  pageContext: { linkCanonical, locale, pageIsMultilingual, schemaMarkupJson, pageName },
  ...props
}: INewToCanadaPageProps) => {
  useAddCookiesFromUrlQueryParams({ url: deeplink });

  return (
    <Layout
      ctaToNavbar={deeplink ?? undefined}
      location={props.location}
      pageIsMultilingual={pageIsMultilingual}
      pageName={pageName}
    >
      <SEO
        canonical={linkCanonical}
        title={metadata.title}
        description={metadata.description}
        imageSrc={metadata.image?.asset?.gatsbyImageData?.images?.fallback?.src}
        keywords={metadata.keywords}
        location={props.location}
        multilingual={pageIsMultilingual}
        structuredData={schemaMarkupJson}
      />
      <Hero deeplink={deeplink} pageName={pageName} {...hero} />
      <About {...about} />
      <Description {...description} />
      <Benefits benefits={benefits} />
      <Instructions
        instructions={page.instructions}
        ctaDescription={page.instructionsLinkDescription}
        ctaText={page.instructionsCtaText}
        ctaUrl={page.instructionsCtaUrl}
        heading={page.instructionsHeading}
      />
      <Resources heading={page.resourcesTitle} resources={page.resources} />
      <Section className="py-64 md:pb-96 md:pt-64">
        <Container className="px-32 xs:px-0">
          <SignUp
            inverse
            {...signUp}
            btnProps={{
              to: deeplink ?? undefined,
              buttonLocation: `${pageName} CTA`,
              queryParams: afTrackingByPage('NEWTOCANADA', 'MUrEdRGl'),
              trackEventProps: {
                category: pageName,
                label: `${pageName} CTA`,
                locale,
              },
              buttonVariant: 'primaryInverse',
            }}
          />
        </Container>
      </Section>

      <Section className="pb-64 md:pb-96">
        <FAQ {...faq} />
      </Section>
    </Layout>
  );
};

export default PageNewToCanada;

export const queryGraphQL = graphql`
  query ($_id: String!) {
    page: sanityPageNewToCanada(_id: { eq: $_id }) {
      about {
        description: _rawDescription
        heading: _rawHeading
        image {
          altText
          img {
            asset {
              gatsbyImageData(placeholder: NONE, width: 2000)
            }
          }
        }
      }
      benefits {
        btnText
        btnLink
        content: _rawContent
        img {
          asset {
            gatsbyImageData(placeholder: NONE, width: 1200)
          }
        }
        imgAltText
      }
      deeplink
      description {
        cards {
          description: _rawDescription
          heading: _rawHeading
          image {
            altText
            img {
              asset {
                gatsbyImageData(placeholder: NONE, width: 1200)
              }
            }
          }
        }
        heading: _rawHeading
      }
      faq {
        ...sanityFAQ
      }
      hero {
        ctaText
        heading: _rawHeading
        image {
          altText
          img {
            asset {
              gatsbyImageData(placeholder: NONE, width: 2000)
            }
          }
        }
        subheading: _rawSubheading
      }
      instructions {
        img {
          asset {
            gatsbyImageData(placeholder: NONE, width: 64)
          }
        }
        imgAltText
        label
        text
      }
      instructionsCtaText
      instructionsCtaUrl
      instructionsHeading: _rawInstructionsHeading
      instructionsLinkDescription
      metadata {
        ...sanityMetadata
      }
      resources {
        excerpt: _rawExcerpt
        img {
          asset {
            url
            gatsbyImageData(placeholder: NONE, width: 610, outputPixelDensities: 2)
          }
        }
        imgAltText
        minutesToRead
        slug {
          current
        }
        subfolder
        title
        locale
      }
      resourcesTitle: _rawResourcesTitle
      signUp {
        ...sanitySignUp
      }
    }
  }
`;
